/**
 * KB Files API — RTK Query Endpoints
 *
 * All API endpoints for the Helpdesk Knowledge Base File Management feature.
 * Uses baseApi.injectEndpoints() following the project's standard pattern.
 *
 * Phase 1: listKBFiles is active.
 * Future phases will uncomment/activate the remaining endpoints.
 *
 * Base path: /helpdesk/knowledge-base/files
 */

import { baseApi } from "./baseApi";
import { buildListQueryParams } from "../../pages/Crm/components/HelpDesk/components/KnowledgeBase/FileManagement/utils/fileManagement.utils";
import type {
  KBFileListParams,
  CreateFolderPayload,
  PatchFilePayload,
  MoveFilesPayload,
  DeleteFilesPayload,
  RequestZipPayload,
  ZipJobResponse,
  UploadFilesResponse,
} from "../../pages/Crm/components/HelpDesk/components/KnowledgeBase/FileManagement/types/fileManagement.types";

const KB_FILES_BASE = "/helpdesk/knowledge-base/files";

const kbFilesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // -----------------------------------------------------------------
    // Phase 1 — Active
    // -----------------------------------------------------------------

    /** GET /helpdesk/knowledge-base/files — paginated list with filters */
    listKBFiles: builder.query({
      query: (params: KBFileListParams) => ({
        url: KB_FILES_BASE,
        params: buildListQueryParams(params),
      }),
      providesTags: [{ type: "KBFiles", id: "LIST" }],
    }),

    // -----------------------------------------------------------------
    // Scaffolded — Will be activated in future phases
    // -----------------------------------------------------------------

    /** GET /helpdesk/knowledge-base/files/:file_id */
    getKBFile: builder.query({
      query: (fileId: number) => ({
        url: `${KB_FILES_BASE}/${fileId}`,
      }),
    }),

    /** POST /helpdesk/knowledge-base/files/folders */
    createFolder: builder.mutation({
      query: (body: CreateFolderPayload) => ({
        url: `${KB_FILES_BASE}/folders`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "KBFiles", id: "LIST" }],
    }),

    /** POST /helpdesk/knowledge-base/files/upload (multipart/form-data) */
    uploadFiles: builder.mutation<UploadFilesResponse, FormData>({
      query: (formData) => ({
        url: `${KB_FILES_BASE}/upload`,
        method: "POST",
        body: formData,
        headers: {
          "Idempotency-Key": crypto.randomUUID(),
        },
      }),
      invalidatesTags: [{ type: "KBFiles", id: "LIST" }],
    }),

    /** PATCH /helpdesk/knowledge-base/files/:file_id */
    patchFile: builder.mutation({
      query: ({
        file_id,
        name,
        visibility,
        file_expiration_time,
      }: PatchFilePayload) => ({
        url: `${KB_FILES_BASE}/${file_id}`,
        method: "PATCH",
        body: { name, visibility, file_expiration_time },
      }),
      // Name shows in both views; visibility only shows in list view.
      invalidatesTags: (_result, _error, { name, visibility, viewMode }) => {
        const shouldRefetch = name || (visibility && viewMode === "list");
        return shouldRefetch ? [{ type: "KBFiles", id: "LIST" }] : [];
      },
    }),

    /** POST /helpdesk/knowledge-base/files/move */
    moveFiles: builder.mutation({
      query: (body: MoveFilesPayload) => ({
        url: `${KB_FILES_BASE}/move`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "KBFiles", id: "LIST" }],
    }),

    /** POST /helpdesk/knowledge-base/files/delete */
    deleteFiles: builder.mutation({
      query: (body: DeleteFilesPayload) => ({
        url: `${KB_FILES_BASE}/delete`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "KBFiles", id: "LIST" }],
    }),

    /** POST /helpdesk/knowledge-base/files/zip */
    requestZip: builder.mutation<ZipJobResponse, RequestZipPayload>({
      query: (body) => ({
        url: `${KB_FILES_BASE}/zip`,
        method: "POST",
        body,
      }),
    }),

    /** GET /helpdesk/knowledge-base/files/zip/:job_id */
    pollZipJob: builder.query<ZipJobResponse, string>({
      query: (jobId) => ({
        url: `${KB_FILES_BASE}/zip/${jobId}`,
      }),
    }),

    /** GET /helpdesk/knowledge-base/files/:file_id/download */
    downloadKBFile: builder.mutation<Blob, number>({
      query: (fileId) => ({
        url: `${KB_FILES_BASE}/${fileId}/download`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useListKBFilesQuery,
  useGetKBFileQuery,
  useCreateFolderMutation,
  useUploadFilesMutation,
  usePatchFileMutation,
  useMoveFilesMutation,
  useDeleteFilesMutation,
  useRequestZipMutation,
  usePollZipJobQuery,
  useDownloadKBFileMutation,
} = kbFilesApi;
